import {
  DistanceUnit,
  SpeedUnit,
  distanceFromMetresConversion,
  speedConversion,
} from '@cas/utils/numberHelpers';
import { useCallback } from 'react';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useTranslation } from 'react-i18next';
import { EmptyCell, GenericValueFormatterFunc } from './genericFormatters';
import i18n from '@customer/i18n';
import { TranslationNamespaceBackend } from '@cas/utils/translationHelpers';
import { useAppSelector } from '@customer/store/hooks';
import { MeasurementUnit } from '@customer/store/apis/authApi';
import { MotiveState } from '@customer/store/apis/investigationsApi';

export const useDegreesFormatter = (): GenericValueFormatterFunc<number | null> => {
  const { getNumberText } = useLocaleFormatter();

  const degreesFormatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number' ? `${getNumberText(value)}\u00B0` : EmptyCell;
    },
    [getNumberText],
  );

  return degreesFormatter;
};

// The Decimal Degrees notation is a standard representation and does not require locale conversion.
export const DecimalDegreesFormatter: GenericValueFormatterFunc<number | null> = ({ value }) => {
  // 8 decimal places appears to be the maximum standard precision.
  return typeof value === 'number' ? value.toFixed(8) : EmptyCell;
};

export const useDistanceText = (): ((value: number) => string) => {
  const { t } = useTranslation();
  const { getNumberText } = useLocaleFormatter();
  const units = useAppSelector(state => state.auth.units);

  const getDistanceText = useCallback(
    (metres: number): string => {
      // All distances assumed to be stored in meters. Default to 1 dp.
      if (units === MeasurementUnit.Metric) {
        if (metres >= 1000) {
          const kilometres = distanceFromMetresConversion(metres, DistanceUnit.Kilometers);
          return t('dist.km', { dist: getNumberText(kilometres, 1) });
        } else {
          return t('dist.m', { dist: getNumberText(metres, 1) });
        }
      } else {
        if (metres >= 1700) {
          // one mile + a bit
          const miles = distanceFromMetresConversion(metres, DistanceUnit.Miles);
          return t('dist.mile', { dist: getNumberText(miles, 1) });
        } else {
          const feet = distanceFromMetresConversion(metres, DistanceUnit.Feet);
          return t('dist.feet', { dist: getNumberText(feet, 1) });
        }
      }
    },
    [getNumberText, units, t],
  );

  return getDistanceText;
};

export const useDistanceFormatter = (): GenericValueFormatterFunc<number | null> => {
  const getDistanceText = useDistanceText();

  const distanceFormatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number' ? getDistanceText(value) : EmptyCell;
    },
    [getDistanceText],
  );

  return distanceFormatter;
};

export const useSpeedText = (): ((value: number) => string) => {
  const { t } = useTranslation();
  const { getNumberText } = useLocaleFormatter();
  const units = useAppSelector(state => state.auth.units);

  const getSpeedText = useCallback(
    (speed: number): string => {
      // All distances assumed to be stored in m/s. Default to 1 dp.
      if (units === MeasurementUnit.Metric) {
        const speedKmph = speedConversion(speed, SpeedUnit.mps, SpeedUnit.kmph);
        return t('speed.kmph', { speed: getNumberText(speedKmph, 1) });
      } else {
        const speedMph = speedConversion(speed, SpeedUnit.mps, SpeedUnit.mph);
        return t('speed.mph', { speed: getNumberText(speedMph, 1) });
      }
    },
    [getNumberText, units, t],
  );

  return getSpeedText;
};

export const useSpeedFormatter = (): GenericValueFormatterFunc<number | null> => {
  const getSpeedText = useSpeedText();

  const speedFormatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number' ? getSpeedText(value) : EmptyCell;
    },
    [getSpeedText],
  );

  return speedFormatter;
};

export const EventTypeFormatter: GenericValueFormatterFunc<number | null> = ({ value }) => {
  return typeof value === 'number'
    ? i18n.t([`Enum.EventType.${value}`, 'Unknown'], {
        ns: TranslationNamespaceBackend,
      })
    : EmptyCell;
};

export const MotiveStateFormatter: GenericValueFormatterFunc<number | null> = ({ value }) => {
  return typeof value === 'number'
    ? i18n.t([`Enum.MotiveState.${MotiveState[value]}`, 'Unknown'])
    : EmptyCell;
};

export function useDopFormatter(): GenericValueFormatterFunc<number | null> {
  const { getNumberText } = useLocaleFormatter();

  const formatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number' ? getNumberText(value, 1) : EmptyCell;
    },
    [getNumberText],
  );

  return formatter;
}

export function useMillisFormatter(): GenericValueFormatterFunc<number | null> {
  const { showTimeAndDateWithMillisFromMillis: showTime } = useLocaleFormatter();

  const millisFormatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number' ? showTime(value) : EmptyCell;
    },
    [showTime],
  );

  return millisFormatter;
}

export function usePercentageFormatter(
  maxDecimals: number,
  alreadyNormalized: boolean,
): GenericValueFormatterFunc<number | null> {
  const { getNumberPercentText } = useLocaleFormatter();

  const formatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      return typeof value === 'number'
        ? getNumberPercentText(value, maxDecimals, alreadyNormalized)
        : EmptyCell;
    },
    [alreadyNormalized, getNumberPercentText, maxDecimals],
  );

  return formatter;
}
